// ── FILTROS DE UN EVENTO ───────────────────────────────────────
// El desplegable con el que se elige deporte y liga vivía dentro de
// App.jsx, así que la agencia no podía usarlo: seguía con la tira de
// chips que se desliza de costado, el control que este reemplazó justo
// porque ESCONDE lo que hay. Acá vive una sola vez y las dos pantallas
// lo importan.
//
// Es un módulo aparte de `cuotasDeEvento.jsx` a propósito. Ese es las
// cuotas de un evento; esto es un filtro. No comparten nada más que los
// tokens del tema, y dos archivos con el nombre de lo que son se
// encuentran mejor que uno con el nombre del lugar de donde salieron.
//
// FORMA OBLIGATORIA: se declara como `function Nombre(` a secas y se
// exporta al final con un solo `export {}`. Media docena de pruebas de
// este repo recortan el cuerpo de un componente leyendo el texto crudo
// del archivo y cortando en el próximo `\nfunction `; un
// `export function` es invisible para ese corte y el cuerpo quedaría
// pegado al del componente anterior.
import { useState, useMemo } from "react";
import { oscuro as Q, F_NUM, F_BODY, RADII, SPACING, ov } from "./theme";
import { normalizarTexto } from "./catalogoDeportivo";
import Icon from "./Icon";


// ── Deporte y liga, en dos desplegables ───────────────────────
// Los dos filtros eran dos filas de chips que se deslizaban de costado, y
// el desplazamiento horizontal ESCONDE lo que hay: el jugador ve cuatro
// chips y tiene que adivinar que deslizando aparecen más. Los del final
// no existen para quien no desliza, que es casi todo el mundo. Con 68
// ligas eso son casi todas.
//
// Ahora son dos desplegables, uno al lado del otro, que abren a ancho
// completo: la lista se recorre de arriba abajo —el gesto que el jugador
// ya viene haciendo para leer los partidos— y cada opción ocupa una fila
// entera, que es lo que la hace cómoda de tocar con el pulgar. Cerrados
// ocupan una sola fila entre los dos, así que en un teléfono los partidos
// suben.
//
// Lo que no se puede romper, y es lo que el cambio de control se podría
// llevar puesto: que se vea QUÉ está elegido sin abrir nada. Si los
// botones dijeran "Deporte" y "Liga" a secas, el jugador filtra, se
// olvida, y después mira una lista corta creyendo que no hay partidos.
// Por eso el botón cerrado dice la opción elegida, su cantidad de
// partidos, se tiñe con el acento de la pantalla, y trae al lado la cruz
// que la quita sin abrir nada.
//
// Volver atrás es barato a propósito: esto es presentación pura. Recibe
// las mismas listas que comían las filas de chips —en el mismo orden, que
// lo deciden deportesDisponibles() y ligasDisponibles() en
// catalogoDeportivo.js— y avisa con `onElegir`. Volver a los chips es
// cambiar este componente por las filas; el módulo que filtra no se tocó.
function SelectorDesplegable({ rotulo, todas, opciones, activa, total,
                               color=Q.violet, onElegir }){
  const [abierto,setAbierto]=useState(false);
  const [filtro,setFiltro]=useState("");
  const elegida = opciones.find(o=>o.clave===activa) || null;

  // El buscador es la salida para la cola larga: con 68 ligas, la que
  // tiene un solo partido se alcanza escribiéndola. Con once deportes
  // sería un campo de más antes de lo que el jugador vino a tocar.
  const conBuscador = opciones.length > 12;

  const visibles = useMemo(()=>{
    const q = normalizarTexto(filtro);
    if(!q) return opciones;
    return opciones.filter(o=>normalizarTexto(o.rotulo).includes(q));
  },[opciones,filtro]);

  const cerrar=()=>{ setAbierto(false); setFiltro(""); };
  const elegir=(clave)=>{ onElegir(clave); cerrar(); };

  const fila=(clave,texto,cantidad)=>{
    const puesta = activa===clave;
    return(
      <button key={texto} onClick={()=>elegir(clave)} style={{
        width:"100%",display:"flex",alignItems:"center",gap:SPACING[8],
        background:puesta?`${color}22`:"transparent",
        border:`1px solid ${puesta?color:"transparent"}`,borderRadius:RADII.md,
        padding:"12px 12px",cursor:"pointer",textAlign:"left",
      }}>
        <span style={{flex:1,minWidth:0,color:puesta?color:Q.text,fontSize:14,
          fontWeight:puesta?700:400,fontFamily:F_BODY,overflow:"hidden",
          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{texto}</span>
        <span style={{color:Q.dim,fontSize:12,fontWeight:700,
          fontFamily:F_NUM}}>{cantidad}</span>
        {puesta&&<Icon name="circle-check" size={14} color={color}/>}
      </button>
    );
  };

  return(<>
    {/* El que tiene algo puesto se queda con el ancho que sobra: su
        rótulo es un nombre de liga o de deporte y hay que poder leerlo,
        mientras el otro dice "Todos" o "Todas" y entra en cualquier
        lado. Con los dos a la mitad exacta, "Masculino. Setka Cup" se
        dibujaba "Masculino…", que no dice qué liga es. */}
    <div style={{flex:elegida?2:1,minWidth:0,display:"flex",alignItems:"center",
      gap:SPACING[4]}}>
      <button onClick={()=>setAbierto(true)}
        aria-label={`${rotulo}: ${elegida?elegida.rotulo:todas}`} style={{
        flex:1,minWidth:0,display:"flex",alignItems:"center",gap:SPACING[4],
        background:elegida?`linear-gradient(135deg,${color}44,${color}22)`:ov(0.04),
        border:`1px solid ${elegida?color:Q.border}`,borderRadius:RADII.md,
        padding:"8px 8px",cursor:"pointer",color:elegida?color:Q.muted,
        fontSize:13,fontWeight:700,fontFamily:F_BODY,textAlign:"left",
      }}>
        {/* Sin la cantidad adentro: el total ya está en el encabezado de
            la pantalla y el de cada opción adentro de la hoja, y acá
            costaba justo el lugar que necesita el nombre. */}
        <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",
          whiteSpace:"nowrap"}}>{elegida?elegida.rotulo:todas}</span>
        <Icon name="chevron-right" size={13}/>
      </button>
      {elegida&&(
        <button onClick={()=>onElegir(null)}
          aria-label={`Quitar el filtro de ${rotulo.toLowerCase()}`}
          style={{flexShrink:0,width:28,height:28,borderRadius:RADII.md,
            background:ov(0.04),border:`1px solid ${Q.border}`,cursor:"pointer",
            color:Q.muted,fontSize:12,lineHeight:1,display:"flex",
            alignItems:"center",justifyContent:"center",padding:0}}>✕</button>
      )}
    </div>

    {/* La hoja abre a ancho completo: cada opción es una fila entera, que
        es lo que la hace cómoda de leer y de tocar con el pulgar. */}
    {abierto&&(
      <div onClick={cerrar} style={{position:"fixed",inset:0,zIndex:1000,
        background:"rgba(5,9,20,0.88)",display:"flex",alignItems:"flex-end"}}>
        <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:520,
          margin:"0 auto",background:Q.deep,borderTop:`1px solid ${Q.border}`,
          borderRadius:"18px 18px 0 0",maxHeight:"80vh",display:"flex",
          flexDirection:"column",
          padding:"16px 16px calc(16px + env(safe-area-inset-bottom))"}}>

          <div style={{width:36,height:4,borderRadius:RADII.sm,background:Q.border,
            margin:"0 auto 16px",flexShrink:0}}/>

          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",gap:SPACING[8],marginBottom:12,flexShrink:0}}>
            <span style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>{rotulo}</span>
            <button onClick={cerrar} aria-label="Cerrar" style={{background:"transparent",
              border:"none",color:Q.muted,fontSize:26,cursor:"pointer",
              lineHeight:1,padding:0}}>×</button>
          </div>

          {conBuscador&&(
            <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
              background:ov(0.05),border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",marginBottom:8,flexShrink:0}}>
              <Icon name="scan-search" size={14} color={Q.muted}/>
              <input value={filtro} onChange={e=>setFiltro(e.target.value)}
                placeholder={`Buscar ${rotulo.toLowerCase()}...`}
                style={{background:"transparent",border:"none",color:Q.text,
                  fontSize:14,flex:1,minWidth:0,fontFamily:F_BODY}}/>
              {filtro&&(
                <button onClick={()=>setFiltro("")} aria-label="Borrar la búsqueda"
                  style={{background:"transparent",border:"none",color:Q.muted,
                    fontSize:16,cursor:"pointer",padding:0,lineHeight:1}}>✕</button>
              )}
            </div>
          )}

          {/* La lista se recorre de arriba abajo y se ve entera: es lo
              que la fila de chips no podía hacer. */}
          <div style={{flex:1,minHeight:0,overflowY:"auto",
            WebkitOverflowScrolling:"touch"}}>
            {!filtro.trim()&&fila(null,todas,total)}
            {visibles.map(o=>fila(o.clave,o.rotulo,o.eventos))}
            {visibles.length===0&&(
              <div style={{color:Q.muted,fontSize:13,textAlign:"center",
                padding:"20px 12px",fontFamily:F_BODY}}>
                No hay ninguna con ese nombre.
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </>);
}

export { SelectorDesplegable };
